import React, { useState } from 'react';

import type { ApiClient } from '../api/client';
import { Dialog } from './Dialog';
import ErrorBanner from './ErrorBanner';
import type { Conversation, ConversationModelOption, AppItem } from '../types';
import { formatModelLabel } from '../utils/format';
import { reportErrorLocal } from '../utils/errors';

const STORAGE_KEY = 'last_fix_bug_preferences';

export const FIX_BUG_WORKFLOW_INSTRUCTIONS = `Workflow:
1. Reproduce the issue with a focused failing test first. If a red test is not practical, explain why before changing production code.
2. Fix the root cause, keeping the change scoped.
3. Run the focused test, then the relevant broader checks.
4. Commit the fix, push the branch with plain git push, then call merge_requests.create_or_reuse for the source and target branch.`;

export type FixBugConversationPayload = {
  title: string;
  app_ids: string[];
};

export function buildFixBugConversationPayload(title: string): FixBugConversationPayload {
  return {
    title: title.trim(),
    app_ids: []
  };
}

interface StoredPreferences {
  model_id?: string;
}

function getStoredPreferences(): StoredPreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (err) {
    console.error('Failed to read fix bug preferences from localStorage', err);
  }
  return null;
}

function setStoredPreferences(prefs: StoredPreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (err) {
    console.error('Failed to save fix bug preferences to localStorage', err);
  }
}

export type FixBugDialogProps = {
  open: boolean;
  onClose: () => void;
  apiClient: ApiClient;
  models: ConversationModelOption[];
  apps: AppItem[];
  defaultTitle?: string;
  defaultAppId?: string;
  defaultPrompt: string;
  onCreated: (conversation: Conversation) => void;
  setError: (message: string | null) => void;
};

export function FixBugDialog({
  open,
  onClose,
  apiClient,
  models,
  defaultTitle,
  defaultPrompt,
  onCreated,
  setError
}: FixBugDialogProps) {
  const [selectedModelId, setSelectedModelId] = useState<string>(() => {
    const stored = getStoredPreferences();
    return stored?.model_id ?? '';
  });
  const [title, setTitle] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update title and prompt when dialog opens
  React.useEffect(() => {
    if (open) {
      setTitle(defaultTitle ?? 'Fix Bug');
      setPrompt(defaultPrompt);
    }
  }, [open, defaultTitle, defaultPrompt]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!title.trim()) {
      setFormError('Title is required');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const created = await apiClient.post<Conversation>(
        '/api/conversations',
        buildFixBugConversationPayload(title)
      );

      await apiClient.post(`/api/conversations/${created.id}/messages`, {
        content: prompt.trim(),
        auto_process: true,
        model_id: selectedModelId || null
      });

      setStoredPreferences({ model_id: selectedModelId });
      setPrompt('');
      onCreated(created);
      onClose();
    } catch (err) {
      reportErrorLocal('Failed to start conversation', err, setError, setFormError);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog
      title="Start Fix Bug Conversation"
      open={open}
      onClose={onClose}
      size="large"
      closeOnOverlayClick={!isSubmitting}
      footer={
        <div className="row">
          <button type="submit" form="fix-bug-form" disabled={isSubmitting}>
            {isSubmitting ? 'Starting...' : 'Start Conversation'}
          </button>
          <button type="button" className="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      }
    >
      <form id="fix-bug-form" onSubmit={handleSubmit} className="stack">
        {formError && <ErrorBanner message={formError} onDismiss={() => setFormError(null)} />}

        <label className="stack">
          Title
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Conversation title"
            required
          />
        </label>

        <label className="stack">
          Model
          <select
            value={selectedModelId}
            onChange={(e) => setSelectedModelId(e.target.value)}
          >
            <option value="">Default model</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {formatModelLabel(model)}
              </option>
            ))}
          </select>
        </label>

        <label className="stack">
          Message (first message in conversation)
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Enter the initial message..."
            rows={12}
            required
          />
        </label>

        <div className="hint">
          Edit the message above to customize the investigation. The AI will receive this
          as the first message and can use MCP tools to query logs and investigate.
        </div>
      </form>
    </Dialog>
  );
}
